/**
 * Shared security helpers used to remove unsafe HTML/URL handling patterns.
 *
 * Related Aikido findings (repository 1677147 - hiver-website):
 *   - Using document write methods can lead to XSS attacks (issue 20338763)
 *   - Using dangerouslySetInnerHTML in React can lead to XSS attacks (issue 20339258)
 *   - Potential Cross Site Scripting (XSS) via window.location.href (issue 20772518)
 *
 * Every helper below is a drop-in replacement for the pattern it replaces, so
 * existing (including legacy) rendering and navigation behaviour is unchanged.
 */

/** Escape plain text so it can be safely interpolated into an HTML string. */
export const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

/** Escape RegExp metacharacters so a user supplied search term is matched literally. */
const escapeRegExp = (value) =>
  String(value ?? "").replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

/**
 * Parse an untrusted HTML string inside an inert document (no browsing context).
 * Scripts never execute and no sub-resources (img, iframe, ...) are fetched, while
 * the fragment parsing context stays identical to
 * document.createElement(tagName).innerHTML = html, so DOM traversal and
 * textContent extraction return exactly the same result as before.
 */
export const createInertElement = (tagName, html) => {
  const inertDocument = document.implementation.createHTMLDocument("");
  const element = inertDocument.createElement(tagName);
  element.innerHTML = String(html ?? "");
  return element;
};

/**
 * Wrap every match of pattern inside content with <tag> and HTML-escape everything
 * else, so search-result highlighting can no longer inject markup or scripts.
 * The pattern is matched literally, so a user supplied search term can neither
 * throw nor cause catastrophic backtracking (ReDoS).
 */
export const highlightMatches = (content, pattern, tag = "mark") => {
  const text = String(content ?? "");
  const source = escapeRegExp(pattern);
  if (!source) return escapeHtml(text);
  const regExp = new RegExp(source, "gi");
  let result = "";
  let lastIndex = 0;
  // The escaped pattern contains no capture groups, so the replace callback
  // receives (match, offset, string).
  text.replace(regExp, (match, offset) => {
    result += escapeHtml(text.slice(lastIndex, offset));
    result += "<" + tag + ">" + escapeHtml(match) + "</" + tag + ">";
    lastIndex = offset + match.length;
    return match;
  });
  return result + escapeHtml(text.slice(lastIndex));
};

/**
 * Open an externally configured URL in a new tab, restricted to http(s).
 * Blocks javascript:, data: and other dangerous schemes and passes noopener so the
 * opened document cannot reach back through window.opener.
 * The referrer is deliberately preserved so referral analytics keep working.
 */
export const openExternalUrl = (url, target = "_blank") => {
  const raw = String(url ?? "").trim();
  if (!raw) return null;
  const candidate = /^https?:\/\//i.test(raw) ? raw : "https://" + raw;
  let parsed;
  try {
    parsed = new URL(candidate);
  } catch (error) {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  return window.open(parsed.href, target, "noopener");
};
